import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from "@reduxjs/toolkit"
import { itemboxAPI, ipeAPI, fieldsAPI } from "../../apis"
import { addErrorNotification } from "../notifications/actions"

export const defaultRequestingContextMenuErrorMessage =
    "Something went wrong when fetching context menu items."

export const fetchItemActions = createAsyncThunk(
    "fetchItemActions",
    async (
        inputData: ItemboxContextMenu.InputData,
        { rejectWithValue, dispatch },
    ) => {
        const { item } = inputData
        try {
            const [flowControlButtons, itemActions] = await Promise.all([
                item.isEditable
                    ? fieldsAPI.collectStateTransitionButtons(item.id)
                    : new Promise<Api.FlowControl[]>((resolve) => resolve([])),
                fieldsAPI.collectItemActions(item.id),
            ])

            return {
                actions: {
                    customActions: itemActions.filter(
                        (action) => action.isCustom,
                    ),
                    standardActions: itemActions.filter(
                        (action) => !action.isCustom,
                    ),
                    buttons: flowControlButtons,
                },
            }
        } catch (error) {
            dispatch(
                addErrorNotification(defaultRequestingContextMenuErrorMessage),
            )
            const responseError: Response = error
            if (!(responseError instanceof Response)) {
                throw error
            }
            return rejectWithValue((await responseError.json()).detail)
        }
    },
)

export const executeItemAction = createAsyncThunk(
    "executeItemAction",
    async ({ itemId, button }: ItemboxContextMenu.ContextMenuCallAction) => {
        const { buttonData, isCustom } = button
        const action = isCustom
            ? `script|${buttonData.actionId}`
            : buttonData.actionId

        return await itemboxAPI.executeCardViewItemAction(itemId, action)
    },
)

export const executeStateTransition = createAsyncThunk(
    "executeStateTransition",
    async ({ itemId, button }: ItemboxContextMenu.StateTransitionAction) => {
        const statusFieldId = 7
        return await ipeAPI.saveIpe(
            {
                object: "Item",
                objectId: itemId,
                propertyId: statusFieldId,
            },
            button.Id,
        )
    },
)

const initialState = {
    item: null,
    targetElement: null,
    isShown: false,
    isStateTransitionLoading: false,
    actions: {
        customActions: [],
        standardActions: [],
        buttons: [],
    },
} as ItemboxContextMenu.State

export const itemBoxContextMenuSlice = createSlice({
    name: "itemBoxContextMenu",

    initialState: initialState,

    reducers: {
        show: (
            state,
            action: PayloadAction<ItemboxContextMenu.SpinnerElement>,
        ) => {
            state.isShown = true
            action.payload?.hideSpinner()
        },
        hide: (state) => {
            state.isShown = false
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchItemActions.pending, (state, action) => {
                state.item = action.meta.arg.item
                action.meta.arg.targetElement?.showSpinner({ replace: true })
            })
            .addCase(fetchItemActions.fulfilled, (state, action) => {
                state.actions = action.payload.actions
                state.isShown = true
                state.isStateTransitionLoading = false
                action.meta.arg.targetElement?.hideSpinner()
            })
            .addCase(fetchItemActions.rejected, (state, action) => {
                console.error(action.payload ?? action.error.message)
                action.meta.arg.targetElement?.hideSpinner()
                state.actions = initialState.actions
                state.item = initialState.item
            })

        builder.addCase(executeItemAction.fulfilled, (state, action) => {
            eval(action.payload)
            state.isShown = false
            state.actions = initialState.actions
            state.item = initialState.item
        })

        builder
            .addCase(executeStateTransition.pending, (state, action) => {
                state.isStateTransitionLoading = true
                state.isShown = false
                action.meta.arg.targetElement?.showSpinner({ replace: true })
            })
            .addMatcher(
                isAnyOf(
                    executeStateTransition.fulfilled,
                    executeStateTransition.rejected,
                ),
                (state, action) => {
                    state.isStateTransitionLoading = false
                    action.meta.arg.targetElement?.hideSpinner()
                    state.actions = initialState.actions
                    state.item = initialState.item
                },
            )
    },
})

export default itemBoxContextMenuSlice.reducer

export const { show, hide } = itemBoxContextMenuSlice.actions
